import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

const STRAPI_BASE_URL = import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337/api';

interface StrapiPagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

interface StrapiMeta {
  pagination: StrapiPagination;
}

interface StrapiApiResponse<T> {
  data: T[];
  meta: StrapiMeta;
}

interface UseStrapiOptions {
  timeout?: number;
  retry?: number;
}

interface UseStrapiResult<T> {
  data: T[] | undefined;
  meta: StrapiMeta | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

const getBackoffDelay = (attempt: number) => Math.min(4000, 1000 * Math.pow(2, attempt - 1));

export function useStrapi<T>(
  endpoint: string,
  locale: string,
  query: string = '',
  page: number = 1,
  pageSize: number = 10,
  options: UseStrapiOptions = {},
): UseStrapiResult<T> {
  const { timeout = 8000, retry = 3 } = options;

  const queryKey = useMemo(
    () => ['strapi', endpoint, locale, query, page, pageSize],
    [endpoint, locale, query, page, pageSize],
  );

  const queryFn = useCallback(async (): Promise<StrapiApiResponse<T>> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    const url = `${STRAPI_BASE_URL}/${endpoint}?locale=${locale}&pagination[page]=${page}&pagination[pageSize]=${pageSize}&${query}`;

    try {
      const res = await fetch(url, { signal: controller.signal });

      if (!res.ok) {
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`Client error: ${res.status}`);
        }

        throw new Error(`Server error: ${res.status}`);
      }

      return res.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('TimeoutError');
      }

      if (err instanceof TypeError) {
        throw new Error('NetworkError');
      }

      throw err;
    } finally {
      clearTimeout(timer);
    }
  }, [endpoint, locale, query, page, pageSize, timeout]);

  const {
    data: apiResponse,
    isLoading,
    error,
    isError,
    refetch,
  } = useQuery<StrapiApiResponse<T>, Error>({
    queryKey,
    queryFn,
    staleTime: 5 * 60 * 1000,

    retry: (failureCount, error) => {
      if (failureCount >= retry) return false;

      const msg = error.message.toLowerCase();

      const shouldRetry = msg.includes('timeout') || msg.includes('network') || msg.includes('server');

      return shouldRetry;
    },

    retryDelay: (attemptIndex) => getBackoffDelay(attemptIndex),
  });

  return {
    data: apiResponse?.data,
    meta: apiResponse?.meta,
    isLoading,
    error: error || null,
    isError,
    refetch,
  };
}
